import { useLiveQuery } from 'dexie-react-hooks'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight,
  Check,
  CircleCheckBig,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
  Wand2,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { useApp } from '@/context'
import type { ComandaLinie, Produs } from '@/db/dexie'
import {
  type MesajParse,
  getProduse,
  parseMesaj,
  totalsFromLinii,
  transformMesajInComanda,
} from '@/db/queries'
import { fmtKg, fmtRON, fmtSuma } from '@/lib/format'
import { parseOrder } from '@/lib/parseOrder'
import { cn } from '@/lib/utils'

interface OrderComposerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mesajId: string | null
}

type HighlightKind = 'match' | 'unmapped' | 'noise' | 'sep'

/**
 * Split the ORIGINAL message on the separators a person actually types
 * (commas, newlines, ";", "+", " și ") and classify each chunk with the real
 * parser, so the BEFORE highlight and the AFTER table always agree.
 */
function buildHighlights(
  text: string,
  produse: Produs[],
  pretSpecial?: Record<string, number>,
): { key: string; text: string; kind: HighlightKind }[] {
  const parts = text.split(/(\s*[\n,;+]\s*|\s+ș?i\s+)/gi)
  return parts.map((part, i) => {
    const key = `h${i}`
    if (i % 2 === 1 || !part.trim()) return { key, text: part, kind: 'sep' as const }
    const { linii, nemapate } = parseOrder(part, produse, { pretSpecial })
    const kind: HighlightKind = linii.length ? 'match' : nemapate.length ? 'unmapped' : 'noise'
    return { key, text: part, kind }
  })
}

const toLinie = (l: ComandaLinie): ComandaLinie => ({
  produsId: l.produsId,
  cod: l.cod,
  nume: l.nume,
  cantitateKg: l.cantitateKg,
  pretKg: l.pretKg,
  um: l.um,
})

export function OrderComposer({ open, onOpenChange, mesajId }: OrderComposerProps) {
  const { openOn, setCaption } = useApp()

  const preview = useLiveQuery<MesajParse | undefined>(
    () => (open && mesajId ? parseMesaj(mesajId) : Promise.resolve(undefined)),
    [open, mesajId],
  )
  const produse = useLiveQuery(getProduse, [], [] as Produs[])

  const [linii, setLinii] = useState<ComandaLinie[]>([])
  const [chips, setChips] = useState<string[]>([])
  const [created, setCreated] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const seeded = useRef<string | null>(null)

  // Seed editable state from the parse preview once per opened message.
  useEffect(() => {
    if (!open) {
      seeded.current = null
      setCreated(null)
      setBusy(false)
      return
    }
    if (preview && seeded.current !== preview.mesaj.id) {
      seeded.current = preview.mesaj.id
      setLinii(preview.linii.map(toLinie))
      setChips([...preview.nemapate])
      setCreated(null)
    }
  }, [open, preview])

  const client = preview?.client
  const produsById = new Map(produse.map((p) => [p.id, p]))
  const totals = totalsFromLinii(linii)
  const highlights =
    preview && produse.length
      ? buildHighlights(preview.mesaj.text, produse, client?.pretSpecial)
      : []

  const pretFor = (p: Produs) => client?.pretSpecial?.[p.id] ?? p.pretKg
  const hasProdus = (id: string) => linii.some((l) => l.produsId === id)

  function addProdus(id: string) {
    const p = produsById.get(id)
    if (!p || hasProdus(id)) return
    setLinii((prev) => [
      ...prev,
      { produsId: p.id, cod: p.cod, nume: p.nume, cantitateKg: 5, pretKg: pretFor(p), um: p.um },
    ])
  }

  function addUzuala(l: ComandaLinie) {
    if (hasProdus(l.produsId)) return
    setLinii((prev) => [...prev, toLinie(l)])
  }

  function resolveChip(chipIndex: number, produsId: string) {
    addProdus(produsId)
    setChips((prev) => prev.filter((_, i) => i !== chipIndex))
  }

  function setQty(index: number, kg: number) {
    setLinii((prev) =>
      prev.map((l, i) => (i === index ? { ...l, cantitateKg: Math.max(0, kg) } : l)),
    )
  }

  function removeLinie(index: number) {
    setLinii((prev) => prev.filter((_, i) => i !== index))
  }

  async function confirma() {
    if (!mesajId || linii.length === 0 || busy) return
    setBusy(true)
    const id = await transformMesajInComanda(mesajId, linii)
    setBusy(false)
    if (id) {
      setCreated(id)
      setCaption(`Comandă ${id} creată din mesaj — fără caiet, fără greșeli.`)
    }
  }

  function close() {
    setCaption(null)
    onOpenChange(false)
  }

  const uzualaDisponibila = (preview?.uzualaLinii ?? []).filter((l) => !hasProdus(l.produsId))

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close()
        else onOpenChange(o)
      }}
    >
      <DialogContent showClose={!created} className="max-w-3xl gap-0 overflow-hidden p-0">
        <DialogHeader className="amber-wash gap-2 border-b border-border px-6 py-5">
          <div className="flex items-center gap-2.5">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-ink">
              <Wand2 className="size-[18px]" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-[1.15rem]">Transformă mesajul în comandă</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-amber-ink" />
                Recunoaștere automată pe bază de reguli — fără AI, fără caiet.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[calc(100dvh-15rem)] overflow-y-auto px-6 py-5">
          {created ? (
            <SuccessPanel
              comandaId={created}
              onView={() => {
                close()
                openOn('comenzi', created)
              }}
              onClose={close}
            />
          ) : (
            <div className="flex flex-col gap-5">
              {/* client recognition */}
              {client && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-pine/25 bg-pine-soft/60 px-3.5 py-2.5 text-sm"
                >
                  <span className="flex size-5 items-center justify-center rounded-full bg-pine text-pine-foreground">
                    <Check className="size-3.5" />
                  </span>
                  <span className="font-medium text-pine">Client recunoscut:</span>
                  <span className="font-semibold text-foreground">{client.nume}</span>
                  <span className="text-muted-foreground">· {client.localitate}</span>
                  {client.pretSpecial && Object.keys(client.pretSpecial).length > 0 && (
                    <span className="ml-auto rounded-md bg-amber-soft px-2 py-0.5 text-xs font-medium text-amber-ink">
                      preț special aplicat
                    </span>
                  )}
                </motion.div>
              )}

              {/* BEFORE → AFTER */}
              <div className="grid gap-4 md:grid-cols-[1fr_auto_1.15fr] md:items-stretch">
                {/* BEFORE */}
                <section className="flex flex-col rounded-xl border border-border bg-secondary/40 p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Mesajul original
                  </h3>
                  <p className="text-[0.95rem] leading-7 text-foreground">
                    {highlights.map((h) =>
                      h.kind === 'sep' ? (
                        <span key={h.key} className="text-muted-foreground/60">
                          {h.text}
                        </span>
                      ) : h.kind === 'noise' ? (
                        <span key={h.key} className="text-muted-foreground/70">
                          {h.text}
                        </span>
                      ) : (
                        <span
                          key={h.key}
                          className={cn(
                            'rounded px-1 py-0.5 font-medium [box-decoration-break:clone] [-webkit-box-decoration-break:clone]',
                            h.kind === 'match'
                              ? 'bg-amber-soft text-amber-ink'
                              : 'bg-tomato-soft text-tomato underline decoration-dotted underline-offset-2',
                          )}
                        >
                          {h.text}
                        </span>
                      ),
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-sm bg-amber-soft ring-1 ring-amber/40" />
                      recunoscut
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-sm bg-tomato-soft ring-1 ring-tomato/40" />
                      de ales manual
                    </span>
                  </div>
                </section>

                {/* arrow */}
                <div className="hidden items-center justify-center md:flex">
                  <motion.span
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 }}
                    className="flex size-9 items-center justify-center rounded-full bg-amber text-amber-ink shadow-paper"
                  >
                    <ArrowRight className="size-[18px]" />
                  </motion.span>
                </div>

                {/* AFTER */}
                <section className="flex flex-col rounded-xl border border-amber/30 bg-card p-4 shadow-paper">
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-ink">
                    <CircleCheckBig className="size-3.5" />
                    Comandă structurată
                  </h3>

                  {/* header row */}
                  <div className="grid grid-cols-[1fr_4.5rem_4rem_5rem_1.5rem] items-center gap-2 border-b border-border pb-1.5 text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                    <span>Produs</span>
                    <span className="text-right">Cant.</span>
                    <span className="text-right">Preț/kg</span>
                    <span className="text-right">Subtotal</span>
                    <span />
                  </div>

                  <div className="flex flex-col divide-y divide-border/70">
                    <AnimatePresence initial={false}>
                      {linii.map((l, i) => {
                        const catalog = produsById.get(l.produsId)
                        const special = catalog ? l.pretKg < catalog.pretKg : false
                        return (
                          <motion.div
                            key={l.produsId}
                            layout
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            className="grid grid-cols-[1fr_4.5rem_4rem_5rem_1.5rem] items-center gap-2 py-2"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-[13px] font-medium leading-tight">
                                {l.nume}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10.5px] text-muted-foreground">
                                  {l.cod}
                                </span>
                                {special && (
                                  <span className="rounded bg-amber-soft px-1 text-[9.5px] font-medium text-amber-ink">
                                    preț special
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                inputMode="decimal"
                                min={0}
                                step={0.5}
                                value={l.cantitateKg}
                                onChange={(e) => setQty(i, Number.parseFloat(e.target.value) || 0)}
                                className="h-7 w-14 px-1.5 text-right text-[13px] nums"
                                aria-label={`Cantitate ${l.nume}`}
                              />
                            </div>
                            <span className="text-right text-[12.5px] text-muted-foreground nums">
                              {fmtSuma(l.pretKg)}
                            </span>
                            <span className="text-right text-[13px] font-semibold text-foreground nums">
                              {fmtSuma(l.cantitateKg * l.pretKg)}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeLinie(i)}
                              className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-tomato-soft hover:text-tomato"
                              aria-label={`Șterge ${l.nume}`}
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </motion.div>
                        )
                      })}
                    </AnimatePresence>

                    {linii.length === 0 && (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Nicio linie încă — adaugă produse mai jos.
                      </p>
                    )}
                  </div>

                  {/* add a line manually */}
                  <div className="mt-2.5">
                    <ProductPicker
                      produse={produse}
                      exclude={new Set(linii.map((l) => l.produsId))}
                      pretFor={pretFor}
                      onPick={addProdus}
                      className="h-auto gap-1.5 border-0 bg-transparent px-1 py-0.5 text-xs font-medium text-amber-ink shadow-none hover:text-amber-strong"
                    >
                      <Plus className="size-3.5" />
                      Adaugă produs
                    </ProductPicker>
                  </div>

                  {/* totals */}
                  <div className="mt-3 flex items-end justify-between border-t border-border pt-3">
                    <div className="text-xs text-muted-foreground">
                      {linii.length} {linii.length === 1 ? 'produs' : 'produse'} ·{' '}
                      <span className="font-medium text-foreground nums">
                        {fmtKg(totals.totalKg)}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Total
                      </div>
                      <div className="font-mono text-xl font-semibold text-amber-ink nums">
                        {fmtRON(totals.totalRON)}
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* nemapate — manual pick chips */}
              {chips.length > 0 && (
                <section className="rounded-xl border border-tomato/25 bg-tomato-soft/40 px-4 py-3">
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-tomato">
                    <TriangleAlert className="size-3.5" />
                    Nerecunoscute — alege produsul
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {chips.map((chip, i) => (
                      <ProductPicker
                        key={`${chip}-${i}`}
                        produse={produse}
                        exclude={new Set(linii.map((l) => l.produsId))}
                        pretFor={pretFor}
                        onPick={(id) => resolveChip(i, id)}
                        className="h-auto gap-1.5 border-tomato/40 bg-card px-2.5 py-1 text-xs font-medium text-tomato hover:bg-tomato-soft"
                      >
                        „{chip}"
                      </ProductPicker>
                    ))}
                  </div>
                </section>
              )}

              {/* comandă uzuală quick-add */}
              {uzualaDisponibila.length > 0 && (
                <section className="rounded-xl border border-border bg-secondary/40 px-4 py-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Comandă uzuală — adaugă rapid
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {uzualaDisponibila.map((l) => (
                      <button
                        key={l.produsId}
                        type="button"
                        onClick={() => addUzuala(l)}
                        className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium shadow-paper transition-colors hover:border-amber/40 hover:bg-amber-soft/50"
                      >
                        <Plus className="size-3.5 text-amber-ink" />
                        {l.nume}
                        <span className="text-muted-foreground nums">{fmtKg(l.cantitateKg)}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {!created && (
          <div className="flex flex-col-reverse items-center gap-2 border-t border-border bg-secondary/30 px-6 py-4 sm:flex-row sm:justify-between">
            <p className="text-xs text-muted-foreground">
              Verifică și confirmă — comanda intră în flux ca „Nou".
            </p>
            <div className="flex w-full gap-2 sm:w-auto">
              <Button variant="outline" className="flex-1 sm:flex-none" onClick={close}>
                Anulează
              </Button>
              <Button
                className="flex-1 sm:flex-none"
                size="lg"
                disabled={linii.length === 0 || busy}
                onClick={confirma}
              >
                <Check className="size-4" />
                Confirmă comanda
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SuccessPanel({
  comandaId,
  onView,
  onClose,
}: {
  comandaId: string
  onView: () => void
  onClose: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center gap-4 py-8 text-center"
    >
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.05 }}
        className="flex size-16 items-center justify-center rounded-full bg-pine-soft text-pine"
      >
        <CircleCheckBig className="size-8" />
      </motion.span>
      <div>
        <h3 className="font-display text-xl font-medium">Comandă creată</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Mesajul a devenit comanda{' '}
          <span className="font-mono font-semibold text-amber-ink">{comandaId}</span>, gata de
          confirmat în flux. Mesajul e marcat „transformat".
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant="outline" onClick={onClose}>
          Rămân la mesaje
        </Button>
        <Button onClick={onView}>
          Vezi în Comenzi
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </motion.div>
  )
}

function ProductPicker({
  produse,
  exclude,
  pretFor,
  onPick,
  className,
  children,
}: {
  produse: Produs[]
  exclude: Set<string>
  pretFor: (p: Produs) => number
  onPick: (produsId: string) => void
  className?: string
  children: ReactNode
}) {
  const options = produse.filter((p) => !exclude.has(p.id))
  return (
    <Select value="" onValueChange={onPick}>
      <SelectTrigger className={cn('w-fit', className)}>{children}</SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            <span className="font-mono text-[11px] text-muted-foreground">{p.cod}</span>
            <span className="font-medium">{p.nume}</span>
            <span className="ml-auto pl-3 text-muted-foreground nums">{fmtRON(pretFor(p))}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
